import { FC } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Spinner, Stack, Typography } from "@altimateai/lego";
import { IconCheck, IconX, IconAlertTriangle, IconLoader2 } from "@tabler/icons-react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { getPlaygroundScheduleRuns } from "@/helpers/apis";
import { PlaygroundScheduleRun } from "./types";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const statusConfig: Record<string, { icon: typeof IconCheck; color: string; bg: string }> = {
  success: { icon: IconCheck, color: "al-text-green-700", bg: "al-bg-green-100" },
  failed: { icon: IconX, color: "al-text-red-700", bg: "al-bg-red-100" },
  partial: { icon: IconAlertTriangle, color: "al-text-yellow-700", bg: "al-bg-yellow-100" },
  running: { icon: IconLoader2, color: "al-text-blue-700", bg: "al-bg-blue-100" },
};

const RunHistory: FC<{ scheduleId: string }> = ({ scheduleId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["scheduleRuns", scheduleId],
    queryFn: () => getPlaygroundScheduleRuns(scheduleId, { limit: 10 }),
    placeholderData: keepPreviousData,
  });

  const runs = data?.runs || [];

  if (isLoading) {
    return (
      <Stack direction="row" align="center" justify="center" className="al-py-4">
        <Spinner size="sm" />
      </Stack>
    );
  }

  if (runs.length === 0) {
    return (
      <Typography className="al-text-muted-foreground al-text-sm al-py-3 al-text-center">
        No runs yet
      </Typography>
    );
  }

  return (
    <div className="al-space-y-2">
      {runs.map((run: PlaygroundScheduleRun) => {
        const cfg = statusConfig[run.status] || statusConfig.failed;
        const StatusIcon = cfg.icon;
        return (
          <Stack
            key={run.id}
            direction="row"
            align="center"
            gap="sm"
            className="al-text-sm al-py-1.5 al-px-2 al-rounded al-bg-muted/30"
          >
            <span
              className={`al-inline-flex al-items-center al-gap-1 al-px-1.5 al-py-0.5 al-rounded-full al-text-xs ${cfg.bg} ${cfg.color}`}
            >
              <StatusIcon className="al-h-3 al-w-3" />
              {run.status}
            </span>
            <span className="al-text-muted-foreground al-text-xs al-flex-1">
              {dayjs(run.run_at).format("MMM D, YYYY, h:mm A z")}
            </span>
            {(run.sent_count !== null || run.failed_count !== null) && (
              <span className="al-text-xs al-text-muted-foreground">
                {run.sent_count ?? 0} sent{run.failed_count ? `, ${run.failed_count} failed` : ""}
              </span>
            )}
            {run.error_message && (
              <span
                className="al-text-xs al-text-red-600 al-truncate al-max-w-[200px]"
                title={run.error_message}
              >
                {run.error_message}
              </span>
            )}
          </Stack>
        );
      })}
    </div>
  );
};

export default RunHistory;
